import { Download, FileAudio, FileText, FileVideo, History, Loader2, Paperclip, Play, Trash2, Upload, Video, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal, promptModal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { CheckRow, Field } from '@/components/common/FormBits'
import { useAuth } from '@/lib/auth/AuthProvider'
import { fmtDate, fmtDateTime } from '../../../shared/format'
import {
  CONSENT_METHODS,
  fmtSize,
  MEETING_FILE_PERMS as P,
  meetingFileUrl,
  uploadMeetingFile,
  useDeleteMeetingFile,
  useInvalidateMeetingFiles,
  useMeetingFiles,
  useMeetingFileSettings,
  useMeetingFileViews,
  type Consent,
  type FileKind,
  type MeetingFile,
} from '../../meetingFiles'
import type { Meeting } from '../../types'
import { toastError, useCrm } from '../../ui'

type Attendee = { id: string; name: string }

/** Toplantı ekleri ve kayıtları: yükleme (kayıtta müşteri onayı zorunlu), panelde izleme, indirme, gerekçeli silme. */
export function MeetingFiles({ meeting: m, attendees, canEdit }: { meeting: Meeting; attendees: Attendee[]; canEdit: boolean }) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const { staffName } = useCrm()
  const canSee = can(P.attachmentView) || can(P.recordingView) || can(P.attachmentUpload) || can(P.recordingUpload)
  const files = useMeetingFiles(m.id, canSee)
  const del = useDeleteMeetingFile(m.id)
  const [upload, setUpload] = useState<FileKind | null>(null)
  const [playing, setPlaying] = useState<{ file: MeetingFile; url: string } | null>(null)
  const [views, setViews] = useState<MeetingFile | null>(null)
  if (!canSee) return null
  const canUpload = (k: FileKind) => canEdit && can(k === 'recording' ? P.recordingUpload : P.attachmentUpload)

  const open = async (f: MeetingFile, download: boolean) => {
    try {
      const url = await meetingFileUrl(m.id, f.id, download)
      if (download) {
        const a = document.createElement('a')
        a.href = url
        a.download = f.name
        a.click()
      } else if (f.kind === 'recording' || f.mime.startsWith('image/') || f.mime === 'application/pdf') {
        if (f.kind === 'recording') setPlaying({ file: f, url })
        else window.open(url, '_blank', 'noopener')
      }
    } catch (e) {
      toastError(e)
    }
  }
  const remove = async (f: MeetingFile) => {
    const uploading = f.status === 'uploading'
    const reason = uploading
      ? ''
      : await promptModal({ title: t('crm.files.deleteTitle', { name: f.name }), description: t('crm.files.deleteHint'), confirmLabel: t('common.delete') })
    if (!uploading && !reason?.trim()) return
    del.mutate({ fileId: f.id, reason: reason ?? '' }, { onSuccess: () => toast.success(t('crm.files.deleted')), onError: toastError })
  }

  const list = files.data ?? []
  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold">
          <Paperclip className="size-4 text-primary" aria-hidden />
          {t('crm.files.title')}
        </h3>
        <div className="flex gap-1.5">
          {canUpload('attachment') ? (
            <Button size="sm" variant="outline" onClick={() => setUpload('attachment')}>
              <Upload />
              {t('crm.files.addAttachment')}
            </Button>
          ) : null}
          {canUpload('recording') ? (
            <Button size="sm" variant="outline" onClick={() => setUpload('recording')}>
              <Video />
              {t('crm.files.addRecording')}
            </Button>
          ) : null}
        </div>
      </div>
      {files.isLoading ? <Skeleton className="h-12" /> : null}
      {!files.isLoading && !list.length ? <p className="text-xs text-muted-foreground">{t('crm.files.empty')}</p> : null}
      {list.map((f) => {
        const Icon = f.kind === 'recording' ? (f.mime.startsWith('audio/') ? FileAudio : FileVideo) : FileText
        return (
          <div key={f.id} className="flex items-start gap-3 rounded-lg border p-3">
            <Icon className="mt-0.5 size-5 shrink-0 text-muted-foreground" aria-hidden />
            <div className="min-w-0 flex-1 space-y-0.5">
              <p className="truncate text-sm font-medium">{f.name}</p>
              <p className="text-xs text-muted-foreground">
                {fmtSize(f.size)} · {staffName(f.uploadedBy)} · {fmtDateTime(f.createdAt)}
              </p>
              {f.status === 'uploading' ? (
                <Badge tone="warning">{t('crm.files.incomplete', { done: f.uploadedParts, total: f.partCount })}</Badge>
              ) : null}
              {f.consent ? (
                <p className="text-xs text-muted-foreground">
                  {t('crm.files.consentLine', {
                    by: f.consent.by || attendees.find((a) => a.id === f.consent?.contactId)?.name || '—',
                    method: t(`crm.files.consentMethods.${f.consent.method}`),
                    at: fmtDateTime(f.consent.at),
                  })}
                </p>
              ) : null}
              {f.expiresAt ? <p className="text-xs text-muted-foreground">{t('crm.files.expires', { date: fmtDate(f.expiresAt) })}</p> : null}
              {f.note ? <p className="text-xs">{f.note}</p> : null}
            </div>
            <div className="flex shrink-0 gap-1">
              {f.status === 'ready' && f.kind === 'recording' ? (
                <Button size="icon" variant="ghost" className="size-8" aria-label={t('crm.files.play')} onClick={() => void open(f, false)}>
                  <Play />
                </Button>
              ) : null}
              {f.canDownload ? (
                <Button size="icon" variant="ghost" className="size-8" aria-label={t('crm.files.download')} onClick={() => void open(f, true)}>
                  <Download />
                </Button>
              ) : null}
              {f.status === 'ready' && can(P.audit) ? (
                <Button size="icon" variant="ghost" className="size-8" aria-label={t('crm.files.views')} onClick={() => setViews(f)}>
                  <History />
                </Button>
              ) : null}
              {f.canDelete ? (
                <Button size="icon" variant="ghost" className="size-8 hover:text-destructive" aria-label={t('common.delete')} onClick={() => void remove(f)}>
                  <Trash2 />
                </Button>
              ) : null}
            </div>
          </div>
        )
      })}
      {upload ? <UploadModal meetingId={m.id} kind={upload} attendees={attendees} onClose={() => setUpload(null)} /> : null}
      {playing ? (
        <Modal open size="xl" onOpenChange={(v) => !v && setPlaying(null)} icon={<Play />} title={playing.file.name} description={t('crm.files.playerHint')}>
          {playing.file.mime.startsWith('audio/') ? (
            <audio src={playing.url} controls autoPlay className="w-full" controlsList="nodownload" />
          ) : (
            <video src={playing.url} controls autoPlay className="max-h-[70vh] w-full rounded-lg bg-black" controlsList="nodownload" />
          )}
        </Modal>
      ) : null}
      {views ? <ViewsModal meetingId={m.id} file={views} onClose={() => setViews(null)} /> : null}
    </section>
  )
}

function UploadModal({ meetingId, kind, attendees, onClose }: { meetingId: string; kind: FileKind; attendees: Attendee[]; onClose: () => void }) {
  const { t } = useTranslation()
  const settings = useMeetingFileSettings()
  const refresh = useInvalidateMeetingFiles()
  const [file, setFile] = useState<File | null>(null)
  const [note, setNote] = useState('')
  const [consent, setConsent] = useState<Consent>({ contactId: '', by: '', method: '', at: '', note: '' })
  const [confirmed, setConfirmed] = useState(false)
  const [progress, setProgress] = useState<{ sent: number; total: number } | null>(null)
  const abort = useRef<AbortController | null>(null)
  useEffect(() => () => abort.current?.abort(), [])
  const recording = kind === 'recording'
  const maxBytes = (settings.data?.maxFileMb ?? 2048) * (1 << 20)
  const tooLarge = !!file && file.size > maxBytes
  const consentOk = !recording || ((consent.contactId || consent.by.trim()) && consent.method && consent.at && confirmed)
  const busy = progress !== null

  const start = async () => {
    if (!file || tooLarge || !consentOk) return
    abort.current = new AbortController()
    setProgress({ sent: 0, total: file.size })
    try {
      await uploadMeetingFile({
        meetingId,
        file,
        kind,
        note,
        consent: recording ? consent : undefined,
        signal: abort.current.signal,
        onProgress: (sent, total) => setProgress({ sent, total }),
      })
      toast.success(t('crm.files.uploaded'))
      await refresh(meetingId)
      onClose()
    } catch (e) {
      if (!abort.current.signal.aborted) toastError(e)
      setProgress(null)
      await refresh(meetingId)
    }
  }
  const pct = progress ? Math.floor((progress.sent / Math.max(progress.total, 1)) * 100) : 0

  return (
    <Modal
      open
      size="lg"
      dismissible={!busy}
      onOpenChange={(v) => !v && !busy && onClose()}
      icon={recording ? <Video /> : <Upload />}
      title={recording ? t('crm.files.uploadRecording') : t('crm.files.uploadAttachment')}
      description={t(recording ? 'crm.files.recordingHint' : 'crm.files.attachmentHint', { max: settings.data?.maxFileMb ?? 2048 })}
      footer={
        busy ? (
          <Button variant="cancel" onClick={() => abort.current?.abort()}>
            <X />
            {t('crm.files.cancelUpload')}
          </Button>
        ) : (
          <>
            <Button variant="cancel" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button onClick={() => void start()} disabled={!file || tooLarge || !consentOk}>
              <Upload />
              {t('crm.files.upload')}
            </Button>
          </>
        )
      }
    >
      <div className="space-y-4">
        <Field id="mf-file" label={t('crm.files.file')} error={tooLarge ? t('crm.files.tooLarge', { max: settings.data?.maxFileMb ?? 2048 }) : undefined}>
          <Input
            id="mf-file"
            type="file"
            disabled={busy}
            accept={recording ? 'audio/*,video/*' : '.pdf,.png,.jpg,.jpeg,.webp,.gif,.txt,.csv,.zip,.doc,.docx,.xls,.xlsx,.ppt,.pptx'}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </Field>
        {recording ? (
          <div className="space-y-3 rounded-lg border border-warning/40 bg-warning/5 p-3">
            <p className="text-sm font-medium">{t('crm.files.consentTitle')}</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field id="mf-cby" label={t('crm.files.consentBy')}>
                <Select
                  id="mf-cby"
                  disabled={busy}
                  value={consent.contactId}
                  onChange={(v) => setConsent((c) => ({ ...c, contactId: v }))}
                  options={[{ value: '', label: t('crm.files.consentOther') }, ...attendees.map((a) => ({ value: a.id, label: a.name }))]}
                />
              </Field>
              {!consent.contactId ? (
                <Field id="mf-cname" label={t('crm.files.consentName')}>
                  <Input id="mf-cname" disabled={busy} value={consent.by} maxLength={200} onChange={(e) => setConsent((c) => ({ ...c, by: e.target.value }))} />
                </Field>
              ) : null}
              <Field id="mf-cmethod" label={t('crm.files.consentMethod')}>
                <Select
                  id="mf-cmethod"
                  disabled={busy}
                  value={consent.method}
                  onChange={(v) => setConsent((c) => ({ ...c, method: v as Consent['method'] }))}
                  options={[{ value: '', label: t('common.select') }, ...CONSENT_METHODS.map((x) => ({ value: x, label: t(`crm.files.consentMethods.${x}`) }))]}
                />
              </Field>
              <Field id="mf-cat" label={t('crm.files.consentAt')}>
                <Input id="mf-cat" type="datetime-local" disabled={busy} value={consent.at} onChange={(e) => setConsent((c) => ({ ...c, at: e.target.value }))} />
              </Field>
            </div>
            <Textarea rows={2} disabled={busy} aria-label={t('crm.files.consentNote')} placeholder={t('crm.files.consentNote')} value={consent.note} onChange={(e) => setConsent((c) => ({ ...c, note: e.target.value }))} />
            <CheckRow checked={confirmed} disabled={busy} onChange={(e) => setConfirmed(e.target.checked)}>
              {t('crm.files.consentConfirm')}
            </CheckRow>
          </div>
        ) : null}
        <Field id="mf-note" label={t('crm.files.note')}>
          <Input id="mf-note" disabled={busy} value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} />
        </Field>
        {progress ? (
          <div className="space-y-1">
            <div className="h-2 overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
              <div className="h-full bg-primary transition-[width]" style={{ width: `${pct}%` }} />
            </div>
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground tabular-nums">
              <Loader2 className="size-3 animate-spin" aria-hidden />
              {t('crm.files.progress', { sent: fmtSize(progress.sent), total: fmtSize(progress.total), pct })}
            </p>
          </div>
        ) : null}
      </div>
    </Modal>
  )
}

function ViewsModal({ meetingId, file, onClose }: { meetingId: string; file: MeetingFile; onClose: () => void }) {
  const { t } = useTranslation()
  const { staffName } = useCrm()
  const q = useMeetingFileViews(meetingId, file.id)
  return (
    <Modal open size="md" onOpenChange={(v) => !v && onClose()} icon={<History />} title={t('crm.files.viewsTitle', { name: file.name })}>
      {!q.data ? (
        <Skeleton className="h-24" />
      ) : !q.data.length ? (
        <p className="text-sm text-muted-foreground">{t('crm.files.noViews')}</p>
      ) : (
        <ul className="divide-y text-sm">
          {q.data.map((v, i) => (
            <li key={i} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <span className="font-medium">{staffName(v.userId)}</span>
              <Badge tone={v.action === 'download' ? 'warning' : 'info'}>{t(`crm.files.actions.${v.action}`)}</Badge>
              <span className="text-xs text-muted-foreground tabular-nums">
                {fmtDateTime(v.at)}
                {v.ip ? ` · ${v.ip}` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  )
}
